import * as Sentry from '@sentry/browser';

import type { Alpine } from "alpinejs";
import {ContestForm, ContestStore, Prize, ScanFrame} from "alpine";
import { isValidPhoneNumber } from 'libphonenumber-js'
import { detectObjects } from "./ai.ts";
import {fetchDevice, sendScan} from "./api.ts";
import {dailyCookieStorage} from "./dailyCookieStorage.ts";
import {allPrizes} from "./data/prizes.ts";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const lostTexts = [
    '<p>Dnes to bohužel nevyšlo, ale pro tvoji příští dovolenou doporučujeme navštívit unikátní pivní místo s&nbsp;vlastním pivovarem v&nbsp;srdci vinného regionu v&nbsp;severní Itálii <a href="https://www.baladin.it/en/" target="_blank">Baladin Open Garden Birreria</a>, se kterým Budvar spolupracuje. Vřele doporučujeme i jejich gastronomii anebo účast na některé z&nbsp;jejich letních&nbsp;akcí.</p>',
    '<p>Dnes to bohužel nevyšlo, ale jakmile se příště objevíš v&nbsp;Berlíně doporučujeme navštívit nejlepší pivní zahrádku <a href="https://en.brlo.de/gastronomien/brlo-brwhouse" target="_blank">BRLO Brwhouse</a>. V&nbsp;předchozích letech Budvar spolupracoval s&nbsp;minipivovarem BRLO dokonce tak úzce, že z&nbsp;toho vznikla společná várka ležáku „Ok,&nbsp;Czech!“</p>',
    '<p>Dnes to bohužel nevyšlo, ale pro tvoji příští dovolenou doporučujeme navštívit restauraci <a href="https://cerveceriaeuropa.com/" target="_blank">Cervecería L\'Europe</a> v&nbsp;Madridu ve&nbsp;Španělsku, kde dostaneš Budvar ve skvělé kondici jako ve&nbsp;své oblíbené restauraci v&nbsp;Česku.</p>',
    '<p>Dnes to bohužel nevyšlo, ale pokud ještě nevíš, co bys mohl dělat na konci května, doporučujeme navštívit známý craftový festival <a href="https://bpbw.hu/en/" target="_blank">Budapešť beer week</a> v&nbsp;Maďarsku. Samozřejmě se potkáš i&nbsp;s&nbsp;Budvarem, který se festivalu&nbsp;účastní.</p>',
    '<p>Dnes to bohužel nevyšlo, ale pokud ti chutná pivo a chtěl bys potkat všechny cool sládky z&nbsp;craftových pivovarů na jednom místě, měl bys vyrazit do&nbsp;Portugalska na <a href="https://www.artbeerfest.pt/" target="_blank">Art beer fest</a>, kde bude možné ochutnat skvělé speciály z&nbsp;celého světa včetně&nbsp;Budvaru.</p>',
    '<p>Dnes to bohužel nevyšlo, ale až budeš na výletě u&nbsp;našich sousedů v&nbsp;Rakousku, doporučujeme navštívit restauraci <a href="https://www.instagram.com/praterwirt/?hl=en" target="_blank">Praterwirt</a> ve&nbsp;Vídni, která je naší vlajkovou lodí a&nbsp;můžeš zde vyzkoušet ideální kombinaci Budvar&nbsp;+&nbsp;Wiener&nbsp;Schnitzel.</p>',
    '<p>Dnes to bohužel nevyšlo, ale pro tvoji příští dovolenou doporučujeme navštívit tankovou restauraci <a href="https://krakow.drevnykocur.cz/" target="_blank">Drevny Kocur</a> v&nbsp;českém stylu na okraji Krakowského náměstí v&nbsp;Polsku, kde dostaneš Budvar ve skvělé kondici jako ve své oblíbené restauraci v&nbsp;Česku.</p>',
    '<p>Dnes to bohužel nevyšlo, ale máme pro tebe doporučení, kam by se měl vydat každá pivní nadšenec alespoň jednou za život. <a href="https://mbcc.mikkeller.com/" target="_blank">MBCC</a> je craftový festival v&nbsp;dánské Kodani a&nbsp;samozřejmě i&nbsp;Budvar bude jeho&nbsp;součástí.</p>',
    '<p>Dnes to bohužel nevyšlo, ale pro tvoji příští dovolenou doporučujeme navštívit tankovou restauraci <a href="https://www.tomotankovna.co.uk/" target="_blank">ToMo Tankovna</a> ve&nbsp;Velké Británii na okraji Londýna, kde dostaneš Budvar ve skvělé kondici jako ve své oblíbené restauraci v&nbsp;Česku.</p>',
];

const RequiredLabel = 'budvar_ok';

const stopMediaStream = (stream: MediaStream): void => {
    stream.getTracks().forEach((track) => {
        if (track.readyState == 'live') {
            track.stop();
        }
    });
}

const convertImageDataToBase64 = (imageData: ImageData): string => {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL('image/jpeg', 0.7);
    }
    return '';
}

const isIOS = (): boolean => {
    return (
        [
            "iPad Simulator",
            "iPhone Simulator",
            "iPod Simulator",
            "iPad",
            "iPhone",
            "iPod",
        ].includes(navigator.platform) ||
        (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
}


export function registerContestStore(alpine: Alpine) {
    const store: ContestStore = {
        step: 'form',
        wonPrize: null,
        wonPrizeData: null,

        lostText: lostTexts[Math.floor(Math.random() * lostTexts.length)],

        form: alpine.$persist<ContestForm>({
            name: '',
            email: '',
            phone: '',
            rules: false,
            privacy: false,
        }).as('contestForm').using(window.sessionStorage) as unknown as ContestForm,

        errors: {},

        currentLocation: alpine.$persist(null)
            .as('currentLocation')
            .using(window.sessionStorage) as unknown as GeolocationPosition,
        loadingLocation: false,

        videoElement: null,
        videoStream: null,

        deviceId: alpine.$persist(null)
            .as('deviceId') as unknown as string,

        alreadyContested: alpine.$persist(false)
            .as('alreadyContested')
            .using(dailyCookieStorage) as unknown as boolean,

        alreadyContestedDialog: null,

        submit(): void {

            const resetValidation = () =>{
                this.errors = {};
            }

            resetValidation();

            // validate
            if (this.form.name === '') {
                this.errors.name = 'Vyplňte jméno'
            }

            if (this.form.phone === '' || !isValidPhoneNumber(this.form.phone, 'CZ')) {
                this.errors.phone = 'Vyplňte platný telefon'
            }

            if (this.form.email === '' || !emailRegex.test(this.form.email)) {
                this.errors.email = 'Vyplňte platný e-mail'
            }

            if (!this.form.rules) {
                this.errors.rules = 'Musíte souhlasit s pravidly'
            }

            if (!this.form.privacy) {
                this.errors.privacy = 'Musíte potvrdit seznámení se zásadami'
            }

            if (Object.keys(this.errors).length > 0) {
                 return;
            }

            Sentry.setContext('User', {
                name: this.form.name,
                phone: this.form.phone,
                email: this.form.email,
            })

            this.step = 'location';
        },

        async requestLocation() {
            Sentry.addBreadcrumb({
                category: "contest",
                message: "requestLocation",
                level: "info",
            });

            if (this.loadingLocation) {
                Sentry.addBreadcrumb({
                    category: "contest",
                    message: "requestLocation - location already loading",
                    level: "warning",
                });
                return;
            }

            const options: PositionOptions = {
                enableHighAccuracy: true,
                timeout: 5000,
            }

            const locationMsg = isIOS()
                ? 'Zapněte si prosím Polohové služby v Nastavení telefonu, sekce Soukromí a zabezpečení. Děkujeme'
                : 'Zapněte polohu v nastavení';

            if (navigator.permissions && typeof navigator.permissions.query === 'function') {
                const permissionResult = await navigator.permissions.query({ name: "geolocation" });
                if (permissionResult.state === "denied") {
                    alert(locationMsg);
                    Sentry.addBreadcrumb({
                        category: "contest",
                        message: "requestLocation - denied",
                        level: "warning",
                    });
                    return;
                }
            }

            this.loadingLocation = true;

            navigator.geolocation.getCurrentPosition((position) => {

                Sentry.addBreadcrumb({
                    category: "contest",
                    message: "requestLocation - success",
                    level: "info",
                });

                this.loadingLocation = false;
                this.currentLocation = position;
                this.step = 'camera';
            }, (error) => {
                if (error.code === error.PERMISSION_DENIED || error.code === error.POSITION_UNAVAILABLE) {
                    alert(locationMsg);
                }
                Sentry.captureException("requestLocation - error: " + error.message + " (" + error.code + ")");
                this.loadingLocation = false;
                console.error(error);
            }, options);
        },

        async requestCamera() {

            if (this.videoElement) {
                if (this.videoStream) {
                    stopMediaStream(this.videoStream)
                }
                this.videoStream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: 'environment'},
                })

                this.videoElement.srcObject = this.videoStream;

                await new Promise(async (resolve) => {
                    if (this.videoElement) {
                        this.videoElement.onloadedmetadata = () => resolve(this.videoElement);
                    }
                })

                this.step = 'scan';
                await alpine.nextTick();

                const result = await detectObjects(this.videoElement, RequiredLabel, 0.8);
                if (this.videoStream) {
                    stopMediaStream(this.videoStream)
                }

                if (!this.deviceId) {
                    try {
                        this.deviceId = await fetchDevice();
                    } catch (e) {
                        Sentry.captureException(e);
                    }
                }

                const scan = {
                    ...this.form,
                    deviceId: this.deviceId,
                    position: {
                        latitude: this.currentLocation?.coords.latitude,
                        longitude: this.currentLocation?.coords.longitude,
                        accuracy: this.currentLocation?.coords.accuracy,
                    },
                    status: result.result === 'ok' ? 'need_check' : 'invalid',
                    frames: result.frames.map((frame: ScanFrame) => {
                        return {
                            image: convertImageDataToBase64(frame.frameData),
                            createdAt: frame.createdAt,
                            detectedObjects: frame.detections.map((detectedObject) => {
                                return {
                                    name: detectedObject.label,
                                    reliability: detectedObject.probability,
                                }
                            }),
                        }
                    })
                };

                console.log('Scan result: ', { status: result.result, frames: scan.frames.length })

                if (result.result === 'failed') {
                    this.scanFailed();
                    await sendScan(scan);
                    console.log('Failed scan saved successfully');
                    return;
                }

                this.scanOk();

                let response;
                try {
                    response = await sendScan(scan);
                } catch (e) {
                    Sentry.captureException(e);
                    return;
                }

                console.log('Valid scan saved successfully. Response: ', {
                    status: response.scanResult.status,
                });

                this.form.name = '';
                this.form.email = '';
                this.form.phone = '';
                this.form.rules = false;
                this.form.privacy = false;

                this.alreadyContested = true;

                if (response.scanResult.status === 'lost' || response.scanResult.status === 'saved') {
                    this.showLost();
                } else if (response.scanResult.status === 'won_prize') {
                    this.showWonPrize(response.scanResult.prize);
                }

                this.checkAlreadyContestedDialog();
            }
        },

        scanOk() {
            if (this.videoStream) {
                stopMediaStream(this.videoStream)
            }

            this.step = 'success';
        },

        scanFailed() {
            if (this.videoStream) {
                stopMediaStream(this.videoStream)
            }

            this.step = 'failed';
        },

        showWonPrize(prize: Prize) {
            this.wonPrize = prize;
            this.step = 'won';
            this.wonPrizeData = allPrizes.get(prize) ?? null;
        },

        showLost() {
            this.step = 'lost'
        },

        checkAlreadyContestedDialog(): void {
            if (
                this.alreadyContested
                && this.alreadyContestedDialog
                && !this.alreadyContestedDialog.open
                && !['won', 'lost'].includes(this.step)
            ) {
                this.alreadyContestedDialog.showModal();
            }
        },
    };

    alpine.store('contest', store);
}
